import { ReactNode, useEffect, useRef, useState } from 'react';

import { useLocation } from 'react-router-dom';

import { addDefaultValueGetter } from '@/forms/utils/formatCell';

import { LinearProgress } from '@mui/joy';
import {
  DataGridProProps,
  GridCallbackDetails,
  GridColDef,
  GridEventListener,
  GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro';

import {
  COLUMN_HEADER_HEIGHT,
  COLUMN_HEIGHT,
  ColumnHeaderSortIcon,
  DefaultEmptyMessage,
  EmptyMessage as EmptyMessageComponent,
  SelectRowCheckbox,
  StyledDataGridPro,
} from '../shared/grid';

const ROW_CLASS_SELECTED = 'row-selected';
const ROW_CLASS_SELECTED_FADING = 'row-selected-fading';
export const ROW_CLASS_ERROR_STATE = 'row-error-state';

export const EmptyMessage = EmptyMessageComponent;

export interface TableProps<T> {
  rows: T[];
  autoRowHeight?: boolean;
  rowHeight?: number;
  columns: GridColDef[];
  tableKey?: string | number;
  onRowClick?: GridEventListener<'rowClick'>;
  loading?: boolean;
  loadingData?: boolean;
  emptyMessage?: ReactNode;
  columnHeaderHeight?: number;
  fixedHeight?: boolean;
  noLineStyles?: boolean;
  getRowId?: (row: T) => string | number;
  footer?: ReactNode;
  sortModel?: DataGridProProps['sortModel'];
  disableColumnSorting?: boolean;
  getRowClassName?: DataGridProProps['getRowClassName'];
  sx?: DataGridProProps['sx'];
  disableVirtualization?: boolean;
  onSortModelChange?: DataGridProProps['onSortModelChange'];
  onRowsScrollEnd?: DataGridProProps['onRowsScrollEnd'];
  valueGetter?: (columns: GridColDef<T>[]) => GridColDef<T>[];
  highlightClickedRow?: boolean;
  apiRef?: DataGridProProps['apiRef'];
  checkboxSelection?: DataGridProProps['checkboxSelection'];
  onRowSelectionModelChange?: DataGridProProps['onRowSelectionModelChange'];
  treeData?: DataGridProProps['treeData'];
  getTreeDataPath?: DataGridProProps['getTreeDataPath'];
  groupingColDef?: DataGridProProps['groupingColDef'];
  defaultGroupingExpansionDepth?: DataGridProProps['defaultGroupingExpansionDepth'];
  pinnedRows?: DataGridProProps['pinnedRows'];
  highlightRowsById?: string[];
  slots?: DataGridProProps['slots'];
  slotProps?: DataGridProProps['slotProps'];
  disableColumnMenu?: boolean;
  localeText?: DataGridProProps['localeText'];
  onColumnOrderChange?: (orderedFields: string[]) => void;
  errorMessage?: ReactNode;
  sortingOrder?: DataGridProProps['sortingOrder'];
  sortingMode?: DataGridProProps['sortingMode'];
}

const Table = <T,>({
  apiRef: ref,
  checkboxSelection,
  onRowSelectionModelChange,
  rows = [],
  columns = [],
  onRowClick = () => {},
  autoRowHeight,
  rowHeight,
  loading,
  loadingData,
  emptyMessage = DefaultEmptyMessage,
  columnHeaderHeight,
  noLineStyles,
  getRowId,
  footer = null,
  sortModel,
  onSortModelChange,
  disableColumnSorting = false,
  getRowClassName,
  disableVirtualization,
  sx,
  onRowsScrollEnd,
  valueGetter = addDefaultValueGetter,
  highlightClickedRow = false,
  treeData = false,
  getTreeDataPath,
  groupingColDef,
  defaultGroupingExpansionDepth,
  pinnedRows,
  highlightRowsById,
  tableKey,
  slots,
  slotProps,
  disableColumnMenu = true,
  localeText,
  onColumnOrderChange,
  errorMessage,
  sortingOrder = ['asc', 'desc'],
  sortingMode = 'server',
}: TableProps<T>) => {
  const location = useLocation();
  const [currentSortModel, setCurrentSortModel] = useState(sortModel ?? []);
  const [selectedRowId, setSelectedRowId] = useState<string | number | null>(
    null,
  );
  const [isFading, setIsFading] = useState(false);
  const fadeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const initialLocation = useRef<string | null>(null);

  const handleSortModelChange = (
    newModel: GridSortModel,
    details: GridCallbackDetails,
  ) => {
    setCurrentSortModel(newModel);
    onSortModelChange?.(newModel, details);
  };

  useEffect(() => {
    setCurrentSortModel(sortModel ?? []);
  }, [sortModel]);

  useEffect(() => {
    initialLocation.current = location.pathname + location.search;

    return () => {
      if (fadeTimeoutRef.current) {
        clearTimeout(fadeTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!highlightClickedRow || selectedRowId === null) {
      return;
    }

    const currentLocation = location.pathname + location.search;

    if (
      (currentLocation === initialLocation.current ||
        currentLocation === `${initialLocation.current}/`) &&
      initialLocation.current
    ) {
      if (fadeTimeoutRef.current) {
        clearTimeout(fadeTimeoutRef.current);
      }

      setIsFading(true);

      fadeTimeoutRef.current = setTimeout(() => {
        setIsFading(false);
        setSelectedRowId(null);
        fadeTimeoutRef.current = null;
      }, 2000);
    }
  }, [location, selectedRowId, highlightClickedRow]);

  const enhancedColumns = valueGetter(columns);

  let apiRef = useGridApiRef();
  if (ref?.current) {
    apiRef = ref;
  }

  const handleColumnOrderChange = () => {
    if (!onColumnOrderChange) {
      return;
    }

    const orderedFields = (apiRef.current?.getAllColumns?.() ?? []).map(
      (column: any) => column.field,
    );

    onColumnOrderChange(orderedFields);
  };

  const props = onRowsScrollEnd
    ? {
        onRowsScrollEnd,
      }
    : {};

  const handleRowClick: GridEventListener<'rowClick'> = (
    params,
    event,
    details,
  ) => {
    if (highlightClickedRow) {
      const rowId = getRowId ? getRowId(params.row) : params.id;

      if (isFading) {
        setSelectedRowId(rowId);
      } else {
        setSelectedRowId(selectedRowId === rowId ? null : rowId);
      }

      if (fadeTimeoutRef.current) {
        clearTimeout(fadeTimeoutRef.current);
        fadeTimeoutRef.current = null;
      }
      setIsFading(false);
    }
    onRowClick(params, event, details);
  };

  const enhancedGetRowClassName: DataGridProProps['getRowClassName'] =
    params => {
      const className = getRowClassName?.(params) || '';

      const classNameSet = new Set(className.split(' '));

      const rowIndex = params.indexRelativeToCurrentPage ?? 0;
      if (rowIndex % 2 === 0) {
        classNameSet.add('row-even');
      } else {
        classNameSet.add('row-odd');
      }

      if (highlightClickedRow && selectedRowId !== null) {
        const rowId = getRowId ? getRowId(params.row) : params.id;
        if (rowId === selectedRowId) {
          classNameSet.add(ROW_CLASS_SELECTED);
          if (isFading) {
            classNameSet.add(ROW_CLASS_SELECTED_FADING);
          }
        }
      }

      if (highlightRowsById?.length) {
        if (highlightRowsById?.includes(params.id as string)) {
          classNameSet.add(ROW_CLASS_ERROR_STATE);
        } else {
          classNameSet.delete(ROW_CLASS_ERROR_STATE);
        }
      }

      return [...classNameSet].join(' ');
    };

  return (
    <>
      <StyledDataGridPro
        key={tableKey}
        {...props}
        localeText={localeText}
        defaultGroupingExpansionDepth={defaultGroupingExpansionDepth}
        apiRef={apiRef}
        treeData={treeData}
        getTreeDataPath={getTreeDataPath}
        groupingColDef={groupingColDef}
        checkboxSelection={checkboxSelection}
        onRowSelectionModelChange={onRowSelectionModelChange}
        sx={sx}
        onColumnOrderChange={handleColumnOrderChange}
        getRowClassName={rows.length ? enhancedGetRowClassName : undefined}
        initialState={{
          sorting: {
            sortModel,
          },
        }}
        getRowId={getRowId}
        noLineStyles={noLineStyles}
        rows={rows}
        disableRowSelectionOnClick
        disableColumnMenu={disableColumnMenu}
        disableColumnResize
        columns={enhancedColumns}
        hideFooter={!footer || !rows?.length}
        hideFooterPagination
        columnHeaderHeight={columnHeaderHeight ?? COLUMN_HEADER_HEIGHT}
        rowHeight={
          rowHeight ||
          (!loading && (!rows?.length || autoRowHeight)
            ? undefined
            : COLUMN_HEIGHT)
        }
        getRowHeight={() =>
          rowHeight ||
          (!loading && (!rows?.length || autoRowHeight)
            ? 'auto'
            : COLUMN_HEIGHT)
        }
        onRowClick={(data, event, _) => {
          if (window.getSelection().type === 'Range') {
            return;
          }
          handleRowClick(data, event, _);
        }}
        slots={{
          columnHeaderSortIcon: ColumnHeaderSortIcon,
          noRowsOverlay: () => errorMessage || emptyMessage,
          footer: () => footer,
          baseCheckbox: SelectRowCheckbox,
          ...slots,
        }}
        loading={loading}
        slotProps={{
          loadingOverlay: {
            variant: 'skeleton',
            noRowsVariant: 'skeleton',
          },
          ...slotProps,
        }}
        autoRowHeight={autoRowHeight}
        sortingMode={sortingMode}
        sortModel={currentSortModel}
        onSortModelChange={handleSortModelChange}
        sortingOrder={sortingOrder}
        footer={!!footer}
        disableColumnSorting={disableColumnSorting}
        disableVirtualization={disableVirtualization}
        pinnedRows={pinnedRows}
      />
      {loadingData && (
        <LinearProgress
          sx={{
            position: 'absolute',
            bottom: 0,
            zIndex: 10,
            width: '100%',
            marginLeft: '-24px',
          }}
          data-testid='infinite-loading-data-progress'
        />
      )}
    </>
  );
};

export default Table;
